import {inject, Injectable} from '@angular/core';
import {PlatformService} from "./platform.service";
import {AnalyticsService} from "./analytics.service";

// Sends every console.warn to Amplitude as a `warning` event.
@Injectable({
  providedIn: 'root'
})
export class ConsoleWarningAnalyticsService {
  private readonly platformService = inject(PlatformService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly oldOnWarn = console.warn;
  private warnReentrancyGuard = false;

  private stringifyExcludingCycles(input: unknown): string {
    const cache: unknown[] = [];
    return JSON.stringify(
      input,
      (key, value) =>
        typeof value === "object" && value !== null
          ? cache.includes(value)
            ? undefined // Duplicate reference found, discard key
            : cache.push(value) && value // Store value in our collection
          : value,
      2
    );
  }

  public initialize(): void {
    if (this.platformService.isServer) {
      return;
    }

    console.warn = (...args: unknown[]) => {
      this.oldOnWarn(...args);

      if (this.warnReentrancyGuard) {
        return;
      }
      this.warnReentrancyGuard = true;
      try {
        const [first, ...rest] = args;
        const message =
          first instanceof Error ? first.message :
          typeof first === 'string' ? first :
          first === undefined ? '' :
          this.stringifyExcludingCycles(first);

        const info: Record<string, unknown> = {};
        if (first instanceof Error && first.stack) {
          info['stack'] = first.stack;
        }

        const extras: unknown[] = [];
        for (const arg of rest) {
          if (arg instanceof Error) {
            extras.push(arg.message);
            if (arg.stack) {
              info['stack'] = arg.stack;
            }
          } else if (arg !== null && typeof arg === 'object') {
            Object.assign(info, arg);
          } else {
            extras.push(arg);
          }
        }
        if (extras.length > 0) {
          info['extra'] = extras;
        }

        const properties: Record<string, string> = {message};
        if (Object.keys(info).length > 0) {
          properties['info'] = this.stringifyExcludingCycles(info);
        }

        this.analyticsService.sendEvent('warning', properties);
      } finally {
        this.warnReentrancyGuard = false;
      }
    }
  }
}
